import React, { useEffect } from 'react';
import dayjs from 'dayjs';

import Text from '~components/atoms/Text';
import ActionButton from '~components/atoms/ActionButton';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { MetalTypeEnum } from '~utils/enums';

import SummaryPDF from './SummaryPDF';
import * as SummaryStyles from './CustomerTransferSummary.module.scss';

const CustomerTransferSummary = () => {
    const dispatch = useAppDispatch();

    const customerTransferSummary = useAppSelector((state) => state.customerTransfer.customerTransferSummary);
    const customerTransferDraftId = useAppSelector(state => state.customerTransfer.customerTransferDraftId);
    const isLoadingCreateCustomerTransfer = useAppSelector(state => state.customerTransfer.loadings.createNewCustomerTransfer);

    const { displayName } = useAppSelector(state => state.auth.userDetails);

    useEffect(() => {
        dispatch(combinedActions.customerTransferGetCustomerTransferSummaryAttempt({ id: customerTransferDraftId.draftId }));
    }, [dispatch, customerTransferDraftId?.draftId]);

    const summaryTable = [
        {
            label: 'Transaction Status',
            value: 'Pending',
        },
        {
            label: 'Transaction Date',
            value: dayjs(new Date()).format('DD/MM/YYYY hh:mm A'),
        },
        {
            label: 'Sender Name',
            value: customerTransferSummary.senderName ? customerTransferSummary.senderName : '-',
        },
        {
            label: 'Sender ID Number',
            value: customerTransferSummary.senderIdNo ? customerTransferSummary.senderIdNo : '-',
        },
        {
            label: 'Sender CIF No',
            value: customerTransferSummary.senderCIFNo ? customerTransferSummary.senderCIFNo : '-',
        },
        {
            label: 'Branch',
            value: customerTransferSummary.branchName ? customerTransferSummary.branchName : '-',
        },
        {
            label: 'Branch Code',
            value: customerTransferSummary.branchCode ? customerTransferSummary.branchCode : '-',
        },
        {
            label: 'Region',
            value: customerTransferSummary.regionName ? customerTransferSummary.regionName : '-',
        },
        {
            label: 'Metal Type',
            value: MetalTypeEnum[customerTransferSummary.metalType] ? MetalTypeEnum[customerTransferSummary.metalType] : '-',
        },
        { 
            label: 'Product', 
            value: customerTransferSummary.productName ? customerTransferSummary.productName : '-',
        },
        { 
            label: 'Total Weight (g)', 
            value: Number(customerTransferSummary?.weight ?? 0).toFixed(3),
        },
        { 
            label: 'Receiver Name', 
            value: customerTransferSummary.receiverName ? customerTransferSummary.receiverName : '-',
        },
        { 
            label: 'Receiver ID Number', 
            value: customerTransferSummary.receiverIdNo ? customerTransferSummary.receiverIdNo : '-',
        },
        { 
            label: 'Receiver CIF No', 
            value: customerTransferSummary.receiverCIFNo ? customerTransferSummary.receiverCIFNo : '-',
        },
        { 
            label: 'Branch Teller Name', 
            value: displayName ? displayName : '',
        },
        { 
            label: 'Sales Personnel Name', 
            value: customerTransferSummary.salesPersonnelName ? customerTransferSummary.salesPersonnelName : '',
        },
        { 
            label: 'Referral Name', 
            value: customerTransferSummary.referralName ? customerTransferSummary.referralName : '',
        },
    ];

    const productTableHeader = ['Product Name', 'Total Weight (g)'];
    const productTableData = [
        {
            productName: customerTransferSummary.productName,
            amount: `${Number(customerTransferSummary?.weight ?? 0).toFixed(3)} g`,
        },
    ];

    const handleSubmitForApproval = () => {
        dispatch(combinedActions.customerTransferCreateNewCustomerTransferAttempt({ draftId: customerTransferDraftId.draftId}))
    };

    return (
        <div className={SummaryStyles.container}>
            <table className={SummaryStyles['summary-table']}>
                <tbody>
                    {summaryTable.map((item, index) => (
                        <tr key={index}>
                            <td>{item.label}</td>
                            <td>
                                {item.value === undefined ? '' : `: ${item.value}`}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className={SummaryStyles['product-container']}>
                <Text
                    label='Description'
                    isBold
                />

                <table>
                    <tbody>
                        <tr>
                            {productTableHeader.map((item, index) => (
                                <td key={index}>
                                    {item}
                                </td>
                            ))}
                        </tr>

                        {productTableData.map((item, index) => (
                            <tr key={index}>
                                <td>
                                    {item.productName}
                                </td>

                                <td>
                                    {item.amount}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className={SummaryStyles['button-container']}>
                <SummaryPDF />

                <ActionButton
                    testId='createTransactionDraftSummary-backToForm-button'
                    label='Back to Form'
                    onClick={() => dispatch(combinedActions.customerTransferSetIsFormFilled(false))}
                />

                <ActionButton
                    testId='createTransaction-submitApproval-button'
                    label='Submit for Approval'
                    onClick={handleSubmitForApproval}
                    isLoading={isLoadingCreateCustomerTransfer}
                />
            </div>
        </div>
    );
};

export default CustomerTransferSummary;
